import React, { createContext, useState, useEffect, useCallback, useContext } from 'react'
import api from '../services/api'
import websocketService from '../services/websocket'
import { useAuth } from './AuthContext'

const CurrentActionContext = createContext(null)

// Parse an API datetime to epoch ms, treating naive timestamps as UTC
// (mirrors the ActionHUD parser: no Z/offset suffix → append Z).
const parseApiDateMs = (dateStr) => {
    if (!dateStr) return null
    const hasTz = dateStr.endsWith('Z') || dateStr.includes('+') || /\-\d{2}:?\d{2}$/.test(dateStr)
    return new Date(hasTz ? dateStr : `${dateStr}Z`).getTime()
}

export const CurrentActionProvider = ({ children }) => {
    const [currentAction, setCurrentAction] = useState(null)
    const [isLoading, setIsLoading] = useState(false)
    const { isAuthenticated } = useAuth()

    // Fetch current action from API
    const fetchCurrentAction = useCallback(async () => {
        // No session yet (or expired) — /player/state would 403 and that is not
        // an error the user should hear about (M26: silent no-op).
        if (!localStorage.getItem('access_token')) return
        try {
            setIsLoading(true)
            const { data } = await api.get('/player/state')
            if (data?.current_action) {
                // An action whose end_time has passed is complete even if the
                // server still reports in_progress (e.g. a scavenging run that
                // finished while the WebSocket was down and awaits extraction).
                // This self-heals the HUD from missed WebSocket events.
                const endMs = parseApiDateMs(data.current_action_end_time)
                const timeExpired = endMs != null && Date.now() >= endMs
                setCurrentAction({
                    type: data.current_action,
                    label: data.current_action_label,
                    start_time: data.current_action_start_time,
                    end_time: data.current_action_end_time,
                    died: data.current_action_died,
                    completed:
                        data.current_action_status === 'failed' ||
                        data.current_action_status === 'completed' ||
                        timeExpired
                })
            } else {
                setCurrentAction(null)
            }
        } catch (error) {
            // 403 = not authenticated yet (e.g. token mid-refresh during the auth
            // callback) — expected, stay silent; anything else is a real error.
            if (error.response?.status !== 403) {
                console.error('CurrentActionContext: Failed to fetch current action:', error)
            }
        } finally {
            setIsLoading(false)
        }
    }, [])

    // Set up WebSocket listeners and fetch on mount
    useEffect(() => {
        // Fetch initial state — only once a session exists (M26: no pre-auth 403s)
        if (isAuthenticated) {
            fetchCurrentAction()
        } else {
            setCurrentAction(null)
        }

        // Create handler that will use the latest fetchCurrentAction
        const handleActionEvent = (event) => {
            console.log('CurrentActionContext: Received action event', event.event_type || event.type)
            fetchCurrentAction()
        }

        // Handle completion events - update status instead of closing immediately
        const handleCompletion = (event) => {
            console.log('CurrentActionContext: Action completed', event.event_type || event.type)
            setCurrentAction(prev => prev ? { ...prev, completed: true, died: event.died || false } : null)
        }

        // Handle death events from encounters
        const handleEncounter = (event) => {
            const encounter = event.encounter
            if (encounter?.outcome?.player_survived === false) {
                console.log('CurrentActionContext: Player died in encounter', event)
                setCurrentAction(prev => prev ? { ...prev, completed: true, died: true } : null)
            }
        }

        // Subscribe to action start events
        websocketService.on('scavenging_delayed.run.started', handleActionEvent)
        websocketService.on('gathering.started', handleActionEvent)
        websocketService.on('crafting.job.started', handleActionEvent)

        // Subscribe to encounter events to detect death
        // FIXED: Updated event name from 'scavenging_delayed.encounter' to 'scavenging_delayed.run.encounter'
        websocketService.on('scavenging_delayed.run.encounter', handleEncounter)

        // Subscribe to completion events
        websocketService.on('scavenging_delayed.run.completed', handleCompletion)
        websocketService.on('gathering.completed', handleCompletion)
        websocketService.on('gathering.stopped', handleCompletion)
        websocketService.on('crafting.job.completed', handleCompletion)

        console.log('CurrentActionContext: Listeners registered')

        // Cleanup
        return () => {
            websocketService.off('scavenging_delayed.run.started', handleActionEvent)
            websocketService.off('gathering.started', handleActionEvent)
            websocketService.off('crafting.job.started', handleActionEvent)
            websocketService.off('scavenging_delayed.run.encounter', handleEncounter)
            websocketService.off('scavenging_delayed.run.completed', handleCompletion)
            websocketService.off('gathering.completed', handleCompletion)
            websocketService.off('gathering.stopped', handleCompletion)
            websocketService.off('crafting.job.completed', handleCompletion)
        }
    }, [fetchCurrentAction, isAuthenticated])

    // Reconcile with server truth while a HUD action is displayed. Actions can
    // be terminated outside this tab (extract/collect/stop in another tab or
    // device) with no WebSocket event reaching us — the periodic refetch makes
    // the HUD disappear (or update) without a manual refresh. Polls only while
    // an action is shown; action starts elsewhere arrive via WS start events.
    useEffect(() => {
        if (!isAuthenticated || !currentAction) return

        const interval = setInterval(fetchCurrentAction, 10000)
        const reconcileOnFocus = () => {
            if (!document.hidden) fetchCurrentAction()
        }
        document.addEventListener('visibilitychange', reconcileOnFocus)

        return () => {
            clearInterval(interval)
            document.removeEventListener('visibilitychange', reconcileOnFocus)
        }
    }, [fetchCurrentAction, isAuthenticated, currentAction])

    const value = {
        currentAction,
        isLoading,
        refetch: fetchCurrentAction
    }

    return (
        <CurrentActionContext.Provider value={value}>
            {children}
        </CurrentActionContext.Provider>
    )
}

export const useCurrentActionContext = () => {
    const context = useContext(CurrentActionContext)
    if (!context) {
        throw new Error('useCurrentActionContext must be used within a CurrentActionProvider')
    }
    return context
}
